/**
 * Smooth scroll scripts.
 * 
 * Handles all functionality related to smooth scroll.
 * 
 * TODO: Clean up animation logic.
 *       - Perhaps separate it into styles similar to the rest of the design system.
 *       - Each style gets an initial state and timeline, with the option to hook into batches, perhaps.
 */

/** Dependencies -------------------------------------------- */

// External

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import ScrollSmoother from 'gsap/ScrollSmoother'
import SplitText from 'gsap/SplitText'
import { debounce } from 'throttle-debounce'

gsap.registerPlugin( ScrollTrigger, ScrollSmoother, SplitText )

// Integrations

import { getNestedLinesSplitText } from '../integrations/gsap'

// Design system

import { completionEventName as superheroCompletionEventName } from '../superhero'

import { baseSpacing } from '../blocks/style-guide'
import { getCssVar, nodeListDiff, isNumeric, getNumberPrecision } from '../helpers'

/** The beef ------------------------------------------------ */

/**
 * The setup events to fire off once the smooth scroll is set up.
 */
export const setupEventName = 'smoothScrollSetup'
export const setupEvent = new Event( setupEventName )
export const animateGeneralEventName = 'smoothScrollAnimateGeneral'
export const animateGeneralEvent = new Event( animateGeneralEventName )

/**
 * Smoothscroll class.
 * Provides all smooth scroll and general scroll trigger functionality.
 */
export default class SmoothScroll {

    /** Class setup ----------------------------------------- */

    constructor( className, config = {} ) {

        this.className = className

        this.config = Object.assign(
            {
                speed                            : 1,
                smooth                           : 0.6,
                scrollVelocity                   : 2500,
                fadeUpDuration                   : 0.5,
                cardBatchInterval                : 0.1,
                cardBatchStagger                 : 0.2,
                cardDuration                     : 0.5,
                cardSpeed                        : 1.1,
                cardLag                          : 0,
                manifestoBatchInterval           : 0.1,
                manifestoBatchStagger            : 0.15,
                manifestoDuration                : 0.4,
                manifestoStagger                 : 0.5,
                statisticBatchInterval           : 0.1,
                statisticBatchStagger            : 0.15,
                statisticElementDuration         : 0.4,
                statisticNumberIncrementDuration : 2.25,
                statisticNumberIncrementOffset   : 0.1,
                richTextBatchInterval            : 0.125,
                richTextBatchMax                 : 3,
                richTextBatchStagger             : 0.2,
                richTextFlowDuration             : 0.3,
                richTextFlowStagger              : 0.4,
                richTextFlowGridStagger          : 0.3,
                richTextUnRedactionDuration      : 0.4,
                richTextUnRedactionStagger       : 0.5,
                resizeDebounce                   : 200
            },
            config
        )

        this.animatables = {}
        this.richTextScrollTriggers = []
    }

    init() {

        this.wrapper = document.querySelector( `.${this.className}` )
        this.content = this.wrapper.querySelector( `.${this.className}__content` )

        if ( ! this.wrapper || ! this.content ) return

        this.jumpLinks = document.querySelectorAll( 'a[href^="#"]' )

        /* Query up the animatable elements ------- */

        // Cards

        const cardSelectors = [ '.c-card:not( .c-slide .c-card )' ]
        const cardContainerSelectors = [
            '.c-query__entity',
            '.is-variation-card-list > .wp-block-post',
            '.has-animation-card-container'
        ]

        this.animatables.cards = document.querySelectorAll( cardSelectors.join( ', ' ) )
        this.animatables.cardContainers = document.querySelectorAll( cardContainerSelectors.join( ', ' ) )

        // Manifestos

        const manifestoSelectors = [
            '.c-manifesto',
            '.is-style-manifesto'
        ]

        this.animatables.manifestos = document.querySelectorAll( manifestoSelectors.join( ', ' ) )

        // Statistics

        const statisticSelectors = [
            '.c-statistic'
        ]

        this.animatables.statistics = document.querySelectorAll( statisticSelectors.join( ', ' ) )

        /* Rich text -------------------- */

        const richTextClass = 'is-rich-text'

        const thingsThatAreNotAnimatedRichTextSelectors = [
            '.c-link-list *',
            'blockquote > *',
            '.is-not-animated-rich-text'
        ]

        const thingsThatAreNotAnimatedRichText = document.querySelectorAll( thingsThatAreNotAnimatedRichTextSelectors.join( ', ' ) )

        // Display & Headings

        const richTextHeadingSelectors = [
            `h1.${richTextClass}:not( [class*='is-style-role'] )`,
            `h2.${richTextClass}:not( [class*='is-style-role'] )`,
            `h3.${richTextClass}:not( [class*='is-style-role'] )`,
            `h4.${richTextClass}:not( [class*='is-style-role'] )`,
            `h5.${richTextClass}:not( [class*='is-style-role'] )`,
            `h6.${richTextClass}:not( [class*='is-style-role'] )`,
            `.${richTextClass}.is-style-role-large-display`,
            `.${richTextClass}.is-style-role-medium-display`,
            `.${richTextClass}.is-style-role-small-display`,
            `.${richTextClass}.is-style-role-large-headline`,
            `.${richTextClass}.is-style-role-medium-headline`,
            `.${richTextClass}.is-style-role-small-headline`
        ]        

        // this.animatables.richTextHeadings = nodeListDiff(
        //     document.querySelectorAll( richTextHeadingSelectors.join( ', ' ) ),
        //     thingsThatAreNotAnimatedRichText
        // )

        this.animatables.richTextHeadings = []

        // Unredactions

        const richTextUnRedactionSelectors = [
            `.${richTextClass}.is-style-role-medium-title`,
            `.${richTextClass}.is-style-role-small-title`
        ]

        // this.animatables.richTextUnRedactions = nodeListDiff(
        //     document.querySelectorAll( richTextUnRedactionSelectors.join( ', ' ) ),
        //     thingsThatAreNotAnimatedRichText
        // )

        this.animatables.richTextUnRedactions = []

        // Long forms (large titles, paragraphs and captions)

        const richTextLongFormSelectors = [
            `.${richTextClass}.is-style-role-large-title`,
            `p.${richTextClass}:not( [class*='is-style-role'] )`,
            `.${richTextClass}.is-style-role-paragraph`,
            `.${richTextClass}.is-style-role-caption`,
            '.c-slide__caption',
            `ul.${richTextClass}:not( .is-style-manifesto ) > li`,
            `ol.${richTextClass}:not( .is-style-manifesto ) > li`
        ]

        // this.animatables.richTextLongForms = nodeListDiff(
        //     document.querySelectorAll( richTextLongFormSelectors.join( ', ' ) ),
        //     thingsThatAreNotAnimatedRichText
        // )

        this.animatables.richTextLongForms = []

        // General things usually found within rich text

        const richTextGeneralSelectors = [
            '.gform_wrapper',
            // '.wp-block-buttons',
            // '.wp-block-quote',
            // '.wp-block-embed',
            // '.wp-block-separator',
            // '.wp-block-image:not( .c-slide .wp-block-image )',
            '.c-process',
            '.c-link-list',
            '.has-animation-fade-up'
        ]

        this.animatables.richTextGenerals = document.querySelectorAll( richTextGeneralSelectors.join( ', ' ) )

        /* General ---------------------- */

        this.animatables.parallaxContainers = document.querySelector( '.c-image.has-parallax .c-image__parallax-outer' )
        this.animatables.footerPlaceholder = document.querySelector( '.footer-placeholder' )

        /* Set up animations ---------------------- */
        
        this.setAnimationInitialStates()

        /* Create the smooth scroller ------------- */

        ScrollSmoother.create(
            {
                wrapper            : this.wrapper,
                content            : this.content,
                speed              : this.config.speed,
                smooth             : this.config.smooth,
                ignoreMobileResize : true
            }
        )

        ScrollTrigger.normalizeScroll( true )
        ScrollSmoother.get().paused( true )

        this.setEventBindings()

        window.dispatchEvent( setupEvent )
    }

    setEventBindings() {

        Array.from( this.jumpLinks ).map(
            ( jumpLink ) => jumpLink.addEventListener( 'click', this.onJumpLinkClick.bind( this ) )
        )

        window.addEventListener( superheroCompletionEventName, this.onSuperheroComplete.bind( this ) )
        // window.addEventListener( 'resize', this.onResize.bind( this ), this.config.resizeDebounce )
        // ScrollTrigger.addEventListener( 'refreshInit', this.onScrollTriggerRefreshInit.bind( this ) )
    }

    /** Animation setup ------------------------------------- */

    setAnimationInitialStates() {

        /* General ---------------------- */

        // Cards

        if ( this.animatables.cards.length ) {

            gsap.set( this.animatables.cards, { '--cardPageFoldSize': 0, opacity: 0, y: baseSpacing * 8, scale: 1.075 } )

            for ( const card of this.animatables.cards ) {
                card.timeline = gsap.timeline( { paused: true } )
                    .to( card, { '--cardPageFoldSize': getCssVar( 'cardFullPageFoldSize', card ), opacity: 1, y: 0, scale: 1, duration: this.config.cardDuration } )
            }
        }

        this.setManifestoInitialAnimationStates()
        this.setStatisticInitialAnimationStates()
        this.setFooterInitialAnimationState()    

        /* Rich text -------------------- */

        this.setRichTextInitialAnimationStates()
    }

    setManifestoInitialAnimationStates() {

        for ( const manifesto of this.animatables.manifestos ) {

            const items = manifesto.querySelectorAll( 'li' )

            gsap.set( manifesto, { height: manifesto.clientHeight } )
            gsap.set( items, { color: 'transparent', overflow: 'hidden' } )

            const itemPaddingTimeline = gsap.timeline( { paused: true } )

            for ( const [ index, item ] of Array.from( items ).entries() ) {

                const ogPT = window.getComputedStyle( item, null ).getPropertyValue( 'padding-top' )
                const ogPB = window.getComputedStyle( item, null ).getPropertyValue( 'padding-bottom' )
                const offset = index ? `<+${this.config.manifestoStagger / items.length}` : null

                gsap.set( item, { height: 0, paddingTop: 0, paddingBottom: 0 } )
                itemPaddingTimeline.to( item, { paddingTop: ogPT, paddingBottom: ogPB, duration: this.config.manifestoDuration }, offset )
            }            

            if ( ! manifesto.timeline ) {
                manifesto.timeline = gsap.timeline( { paused: true } )
                    .add( itemPaddingTimeline.play() )
                    .to( items, { height: 'auto', duration: this.config.manifestoDuration, stagger: { amount: this.config.manifestoStagger } }, `<+${this.config.manifestoStagger}` )
                    .to( items, { color: getCssVar( 'textColor', manifesto ), duration: this.config.manifestoDuration, stagger: { amount: this.config.manifestoStagger } }, '<' )
                    .set( items, { clearProps: 'all' } )
                    .set( manifesto, { clearProps: 'all' } )
            }
            else {
                manifesto.timeline.restart().pause()
            }
        }
    }

    setStatisticInitialAnimationStates() {

        for ( const statistic of this.animatables.statistics ) {

            const statNumberElement = statistic.querySelector( '.c-statistic__number' )
            const statNumber = statNumberElement.textContent

            const splitStatistic = this.splitStatistic( statNumber )
            const tweeningStatisticNumericParts = Object.assign( {}, splitStatistic.numericParts )

            Object.keys( tweeningStatisticNumericParts ).forEach( key => tweeningStatisticNumericParts[key] = 0 );

            const updateCallback = this.getStatisticTimelineUpdateCallback( statNumberElement, tweeningStatisticNumericParts, splitStatistic.parts )

            updateCallback()
            gsap.set( statistic, { opacity: 0, y: baseSpacing * 4 } )

            statistic.timeline = gsap.timeline( { paused: true, onUpdate: updateCallback  } )
                .to( statistic, { opacity: 1, y: 0, duration: this.config.statisticElementDuration } )
                .to( tweeningStatisticNumericParts, { ...splitStatistic.numericParts, duration: this.config.statisticNumberIncrementDuration }, `<+${this.config.statisticNumberIncrementOffset}` )
        }

    }

    setFooterInitialAnimationState() {
        gsap.set( this.animatables.footerPlaceholder, { height: document.querySelector( '.c-footer' ).clientHeight } )
    }

    setRichTextInitialAnimationStates() {

        const richTextUnRedactionClass = 'is-rich-text-un-redaction'
        const richTextUnRedactionInitialState = { '--richTextRedactionScaleX': 0, '--richTextRedactionBackgroundScaleX': 1, '--richTextRedactionTransformOrigin': 'left center' }
        const fadeUpInitialState = { opacity: 0, y: 24 }

        const richTextFlowClass = 'is-rich-text-flow'
        const richTextFlowInitialState = { opacity: 0, y: baseSpacing * 2 }

        // Unredactions

        for ( const richTextUnRedactions of Array.from( this.animatables.richTextUnRedactions ) ) {

            richTextUnRedactions.classList.add( richTextUnRedactionClass )

            if ( richTextUnRedactions.splitText ) {
                richTextUnRedactions.splitText.revert()
            }
            richTextUnRedactions.splitText = this.getClassySplitText( richTextUnRedactions, { type: 'words' } )
            gsap.set( richTextUnRedactions.splitText.words, richTextUnRedactionInitialState )

            if ( richTextUnRedactions.timeline ) {
                richTextUnRedactions.timeline.killTweensOf().kill()
            }
            richTextUnRedactions.timeline = this.getRichTextUnRedactionTimeline( richTextUnRedactions.splitText.words )
        }

        // Headings use the flow

        for ( const richTextHeading of Array.from( this.animatables.richTextHeadings ) ) {

            richTextHeading.classList.add( richTextFlowClass )

            if ( richTextHeading.splitText ) {
                richTextHeading.splitText.revert()
            }
            richTextHeading.splitText = this.getClassySplitText( richTextHeading, { type: 'lines, chars' } )
            gsap.set( richTextHeading.splitText.chars, richTextFlowInitialState )

            if ( richTextHeading.timeline ) {
                richTextHeading.timeline.killTweensOf().kill()
            }
            richTextHeading.timeline = this.getRichTextFlowTimeline( richTextHeading.splitText.chars )
        }

        // Long forms and generals just use the fade up

        for ( const richTextFadeUp of [ ...this.animatables.richTextLongForms, ...this.animatables.richTextGenerals ] ) {

            gsap.set( richTextFadeUp, fadeUpInitialState )

            if ( richTextFadeUp.timeline ) {
                richTextFadeUp.timeline.killTweensOf().kill()
            }
            richTextFadeUp.timeline = this.getFadeUpTimeline( richTextFadeUp )
        }
    }    

    /** Main animations ------------------------------------- */

    /**
     * Creates general animations.
     * Things like images, the footer, etc. Basically anything simple.
     */
    animateGeneral() {

        /* Parallax -------------------- */

        // Images

        ScrollSmoother.get().effects(
            this.animatables.parallaxContainers,
            { speed: 'auto' }
        )

        // Cards (or, well, card containers)

        ScrollSmoother.get().effects(
            this.animatables.cardContainers,
            { speed: this.config.cardSpeed, lag: this.config.cardLag }
        )

        /* Scroll triggers ------------- */

        // Cards

        if ( this.animatables.cards.length ) {
            ScrollTrigger.batch(
                this.animatables.cards,
                {
                    interval : this.config.cardBatchInterval,
                    once     : true,
                    onEnter  : batch => this.getAnimatablesBatchTimeline( batch, this.config.cardBatchStagger ).play()
                }
            )
        }

        // Manifestos

        if ( this.animatables.manifestos.length ) {
            ScrollTrigger.batch(
                this.animatables.manifestos,
                {
                    interval : this.config.manifestoBatchInterval,
                    once     : true,
                    onEnter  : batch => this.getAnimatablesBatchTimeline( batch, this.config.manifestoBatchStagger ).play()
                }
            )
        }

        // Statistics

        if ( this.animatables.statistics.length ) {
            ScrollTrigger.batch(
                this.animatables.statistics,
                {
                    interval : this.config.statisticBatchInterval,
                    once     : true,
                    onEnter  : batch => this.getAnimatablesBatchTimeline( batch, this.config.statisticBatchStagger ).play()
                }
            )
        }

        // Everything else can hook in?
        window.dispatchEvent( animateGeneralEvent )
    }

    /**
     * Animates rich text elements.
     * Headings, paragraphs, etc. Mostly the core text blocks from the text editor.
     */
    animateRichText() {

        // We're gonna need to batch these up so they don't just all fire at once.
        // To do this, we batch up all of the elements and then place all of the batched elements' timelines into a larger one.
        // This larger one then fires sequentially according to the stagger props.

        const richTextAnimatables = [
            ...Array.from( this.animatables.richTextHeadings ),
            ...Array.from( this.animatables.richTextUnRedactions ),
            ...Array.from( this.animatables.richTextLongForms ),
            ...Array.from( this.animatables.richTextGenerals )
        ]

        for ( const richTextScrollTrigger of this.richTextScrollTriggers ) {
            richTextScrollTrigger.kill()
        }

        this.richTextScrollTriggers = ScrollTrigger.batch(
            richTextAnimatables,
            {
                interval : this.config.richTextBatchInterval,
                batchMax : this.config.richTextBatchMax,
                once     : true,
                onEnter  : batch => this.getAnimatablesBatchTimeline( batch, this.config.richTextBatchStagger ).play(),
            }
        )
    }

    /** Handlers -------------------------------------------- */

    onSuperheroComplete() {

        ScrollSmoother.get().paused( false )

        if ( window.location.hash ) {
            this.scrollTo( window.location.hash, false )
        }

        this.animateGeneral()
        this.animateRichText()
    }

    onScrollTriggerRefreshInit() {
        // this.setFooterInitialAnimationState()
        // this.setRichTextInitialAnimationStates()
        // this.animateRichText() 
    }

    onResize() {
        // this.setRichTextInitialAnimationStates()
        // this.animateRichText()
    }

    onJumpLinkClick( e ) {

        e.preventDefault()

        const hash = e.target.getAttribute( 'href' )

        history.replaceState( {}, '', hash )
        this.scrollTo( hash )
    }

    /** Timelines ------------------------------------------- */

    getRichTextFlowTimeline( chars, useGridStagger = false ) {

        const defaultStagger = {
            amount : this.config.richTextFlowStagger
        }

        const gridStagger = {
            grid   : 'auto',
            from   : 'center',
            axis   : 'x',
            amount : this.config.richTextFlowGridStagger
        }

        return gsap.timeline( { paused: true } )
            .to( chars, { opacity: 1, y: 0, duration: this.config.richTextFlowDuration, stagger: useGridStagger ? gridStagger : defaultStagger } )
    }

    getRichTextUnRedactionTimeline( entities ) {

        entities = Array.isArray( entities ) ? entities : [ entities ]

        const entityCount = entities.length
        const timeline = gsap.timeline( { paused: true } )

        for ( const [ index, entity ] of entities.entries() ) {

            const entityTimeline = gsap.timeline( { paused: true } )
                .to( entity, { '--richTextRedactionScaleX': 1, duration: this.config.richTextUnRedactionDuration, ease: 'power3.inOut' } )
                .set( entity, { '--richTextRedactionTransformOrigin': 'right center' } )
                .to( entity, { '--richTextRedactionScaleX': 0, '--richTextRedactionBackgroundScaleX': 0, duration: this.config.richTextUnRedactionDuration, ease: 'power3.inOut' } )

            timeline.add( entityTimeline.play(), index ? `<+${this.config.richTextUnRedactionStagger / entityCount}` : null )
        }

        return timeline
    }

    getFadeUpTimeline( elements ) {

        return gsap.timeline( { paused: true } )
            .to( elements, { opacity: 1, y: 0, scale: 1, duration: this.config.fadeUpDuration } )
    }

    getAnimatablesBatchTimeline( animatables, stagger ) {

        const timeline = gsap.timeline( { paused: true } )

        for ( const [ index, animatable ] of animatables.entries() ) {
            timeline.add( animatable.timeline.play(), index ? `<+${stagger}` : null )
        }

        return timeline
    }

    /** Utils ----------------------------------------------- */

    getClassySplitText( element, options = {} ) {
        return new SplitText(
            element,
            { ...options, linesClass: 'rich-text-line', wordsClass: 'rich-text-word', charsClass: 'rich-text-char' }
        )
    }

    splitStatistic( statisticString ) {

        const splitStatistic = { parts: {}, numericParts : {} }
        const parts = statisticString.match( /([,\.\d]+|[^,\.\d]+)/g );

        for ( const [ index, part ] of parts.entries() ) {

            const isPartNumeric = isNumeric( part )
            const resolvedPart = isPartNumeric ? Number( part.replace( /[^\.\d]/g, '' ) ) : part

            splitStatistic.parts[`part${index}`] = resolvedPart

            if ( isPartNumeric ) {
                splitStatistic.numericParts[`part${index}`] = resolvedPart
            }
        }

        return splitStatistic
    }

    getStatisticTimelineUpdateCallback( statisticNumberElement, tweeningStatisticNumericParts, statisticParts ) {

        return () => {
            
            let updatedNumber = ''

            for ( const key of Object.keys( statisticParts ) ) {

                let formattedPart = statisticParts[key]

                if ( key in tweeningStatisticNumericParts ) {

                    // Numbers that were originally integers get rounded to their nearest whole number
                    // Those that aren't get however many decimals they had originally
                    const precision = getNumberPrecision( statisticParts[key] )

                    formattedPart = tweeningStatisticNumericParts[key]
                        .toLocaleString( 'en-US', { maximumFractionDigits: precision } )
                }

                updatedNumber += formattedPart
            }

            statisticNumberElement.textContent = updatedNumber
        }
    }

    scrollTo( target, smooth = true ) {

        const smoother = ScrollSmoother.get()
        const headerOffset = parseInt( getCssVar( 'adminBarHeight' ) ) + parseInt( getCssVar( 'headerHeight' ) )

        const scrollOffset = Math.min( ScrollTrigger.maxScroll( window ), smoother.offset( target, `top ${headerOffset}px` ) )
        const scrollDuration = smooth ? Math.abs( smoother.scrollTop() - scrollOffset ) / this.config.scrollVelocity : 0

        gsap.to( smoother, { scrollTop: scrollOffset, duration: scrollDuration } )
    }

}
